import React, { useRef, useState, useEffect } from "react";
import Datatable from "../../components/shared/datatable/Datatable";
import { PlusSvg } from "../../svgFiles/PlusSvg";
import { DeleteLightRed } from "../../svgFiles/DeleteLightRed";
import { EditBlue } from "../../svgFiles/EditBlue";
import { Invoice } from "../../svgFiles/Invoice";
import { Info } from "../../svgFiles/Info";
import { EyeView } from "../../svgFiles/EyeView";
import { OrangeArrow } from "../../svgFiles/OrangeArrow";
import { Dropdown, Overlay, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router";
import {
  useGetExpensesQuery,
  useUpdateExpenseMutation,
  useRemoveExpenseMutation,
  useGetExpenseByIdQuery,
  useUploadExpenseInvoiceMutation,
  useUploadExpenseReceiptMutation,
  useRemoveExpenseReceiptMutation,
  useRemoveExpenseInvoiceMutation,
} from "./expenseApi";
import { toast } from "react-toastify"; // Assuming you have react-toastify installed
import { convertToUserTimezone, formatNumber, showToast } from "../../helpers/Utils";
import CommonDeleteModal from "../../components/shared/modalLayout/CommonDeleteModal";
import { Routes as AppRoutes } from "../../routes";
import BillViewModal from "../../components/shared/modalContent/BillViewModal";
import useTranslator from "../../hooks/useTranslator";
import { UploadSimpleSvg } from "../../svgFiles/UploadSimpleSvg";

const BillListing = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const [filterParams, setFilterParams] = useState({
    page: 1,
    per_page: 10,
  });

  // Fetch expenses from API
  const {
    data: expenseData,
    isLoading,
    refetch,
    isFetching,
  } = useGetExpensesQuery({
    ...filterParams,
    status: "unpaid", // ✅ force it here too
  });

  const [updateExpense, { isLoading: isUpdating }] = useUpdateExpenseMutation();
  const [removeExpense, { isLoading: isRemoving }] = useRemoveExpenseMutation();
  const [uploadExpenseInvoice, { isLoading: isUploadingInvoice }] =
    useUploadExpenseInvoiceMutation();
  const [uploadExpenseReceipt, { isLoading: isUploadingReceipt }] =
    useUploadExpenseReceiptMutation();

  const [removeExpenseInvoice, { isLoading: isRemovingInvoice }] =
    useRemoveExpenseInvoiceMutation();

  const [removeExpenseReceipt, { isLoading: isRemovingReceipt }] =
    useRemoveExpenseReceiptMutation();

  // State for bill view modal
  const [showBillViewModal, setShowBillViewModal] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const { data: billQueryData, isLoading: isLoadingBillDetails } =
    useGetExpenseByIdQuery(selectedBillId, { skip: !selectedBillId });

  // Add loading state for UI feedback
  const [loadingStatusId, setLoadingStatusId] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [showStatusConfirmationModal, setShowStatusConfirmationModal] =
    useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState({
    id: null,
    bill_number: null,
    newStatus: null,
  });
  const [selectedBillData, setSelectedBillData] = useState(null);
  const [selectedInvoiceRecordId, setSelectedInvoiceRecordId] = useState(null);
  const [selectedReceiptRecordId, setSelectedReceiptRecordId] = useState(null);

  const [showInvoiceDelModal, setShowInvoiceDelModal] = useState(false);
  const [delInvoiceRecordId, setDelInvoiceRecordId] = useState(null);
  const [showReceiptDelModal, setShowReceiptDelModal] = useState(false);
  const [delReceiptRecordId, setDelReceiptRecordId] = useState(null);

  const fileInputRefs = useRef({});
  const receiptFileInputRefs = useRef({});

  // Handle pagination change
  const handlePageChange = (page) => {
    setFilterParams((prev) => ({
      ...prev,
      page,
    }));
  };

  useEffect(() => {
    if (billQueryData?.data) {
      setSelectedBillData(billQueryData.data);
    }
  }, [billQueryData]);

  const handleDeleteExpense = (expense) => {
    setExpenseToDelete((prev) => ({
      ...prev,
      id: expense.id, // update the ID for the tooltip
      bill_number: expense.bill_number, // update the name for the tooltip
    })); // store the selected expense object
    setShowConfirmationModal(true);
  };

  const confirmDelete = async () => {
    if (!expenseToDelete) return;

    try {
      setLoadingStatusId(expenseToDelete.id); // optional: show loading on this row

      const result = await removeExpense(expenseToDelete.id).unwrap();

      if (result && result.success) {
        refetch();
      } else {
        showToast(_t("unable_to_delete_expense", "Unable to delete the expense. Please try again."), "error");
      }
    } catch (error) {
      console.error("Failed to delete expense:", error);
      showToast(_t("unable_to_delete_expense", "Unable to delete the expense. Please try again."), "error");
    } finally {
      setLoadingStatusId(null);
      setShowConfirmationModal(false); // close modal
      setExpenseToDelete(null); // clear selected expense
    }
  };

  const cancelDelete = () => {
    setShowConfirmationModal(false);
    setExpenseToDelete(null);
  };

  // Store the ID of the currently visible tooltip, null if none shown
  const [activeTooltipId, setActiveTooltipId] = useState(null);

  // Create a ref for the tooltip container to help with hovering logic
  const tooltipContainerRefs = React.useMemo(() => {
    const refs = {};
    if (expenseData?.data?.data) {
      expenseData.data.data.forEach((row) => {
        refs[row.id] = React.createRef();
      });
    }
    return refs;
  }, [expenseData?.data?.data]);

  // Store hover state to handle tooltip visibility
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);

  // Create refs for each possible row - we'll use the row ID as the key
  const tooltipTriggerRefs = React.useMemo(() => {
    const refs = {};
    if (expenseData?.data?.data) {
      expenseData.data.data.forEach((row) => {
        refs[row.id] = React.createRef();
      });
    }
    return refs;
  }, [expenseData?.data?.data]);

  // Function to show a specific tooltip
  const showTooltip = (id) => {
    setActiveTooltipId(id);
  };

  // Function to hide all tooltips
  const hideTooltip = (id) => {
    // Only hide if we're not hovering the tooltip content
    if (!isHoveringTooltip) {
      setActiveTooltipId(null);
    }
  };

  const handleChangeStatus = async (id, status) => {
    try {
      // Set loading state for this specific expense
      setLoadingStatusId(id);

      // Create FormData object for the request
      const formData = new FormData();
      formData.append("status", status.toLowerCase());

      // Call the updateExpense mutation with FormData
      const result = await updateExpense({
        id,
        requestBody: formData,
      }).unwrap();

      // Check if the update was successful
      if (result && result.success) {
        // showToast(`Expense status updated to ${status} successfully`);
        // Refetch the expense list to reflect the changes
        refetch();
      } else {
        showToast(
          "Failed to update status: API returned unsuccessful response"
        );
      }
    } catch (error) {
      console.error("Failed to update status:", error);
      showToast(_t("unable_to_update_expense_status", "Unable to update the expense status. Please try again."));
    } finally {
      // Clear loading state
      setLoadingStatusId(null);
    }
  };

  const handleEditExpense = (id) => {
    navigate(`${AppRoutes.BillingReport.path}/${id}`);
  };

  // Handle view expense - open modal with expense details
  const handleViewExpense = (id) => {
    setSelectedBillId(id);
    setShowBillViewModal(true);
  };

  // Close bill view modal
  const handleCloseBillViewModal = () => {
    setShowBillViewModal(false);
    setSelectedBillId(null);
    setSelectedBillData(null);
  };

  const confirmChangeStatusPrompt = (id, bill_number, newStatus) => {
    setPendingStatusChange({ id, bill_number, newStatus });
    setShowStatusConfirmationModal(true);
  };

  const columns = [
    {
      label: _t("bill_number", "Bill Number"),
      accessor: "bill_number",
      cell: ({ row }) => <b className="text-dark">#{row.bill_number}</b>,
    },
    {
      label: _t("vendor", "Vendor"),
      accessor: "vendor_name",
    },
    {
      label: _t("expense_type", "Expense Type"),
      accessor: "expense_type",
      cell: ({ row }) => (
        <span>
          {row.items && row.items.length > 0
            ? _t(row.items[0].expense_type , row.items[0].expense_type)
            : "N/A"}
          <span
            className="cursor-pointer ps-2"
            ref={tooltipTriggerRefs[row.id]}
            onClick={() => {
              if (activeTooltipId === row.id) {
                setActiveTooltipId(null);
              } else {
                showTooltip(row.id);
              }
            }}
            onMouseEnter={() => showTooltip(row.id)}
            onMouseLeave={() => hideTooltip(row.id)}
          >
            {Info}
          </span>
          <Overlay
            target={tooltipTriggerRefs[row.id]?.current}
            show={activeTooltipId === row.id}
            placement="bottom"
            container={tooltipContainerRefs[row.id]?.current}
            containerPadding={20}
            delay={{ show: 0, hide: 500 }}
            rootClose={false}
          >
            {(props) => (
              <Tooltip
                id={`tooltip-${row.id}`}
                {...props}
                className="custom-tooltip"
                onMouseEnter={() => setIsHoveringTooltip(true)}
                onMouseLeave={() => {
                  setIsHoveringTooltip(false);
                  setActiveTooltipId(null);
                }}
              >
                <div className="tooltip-content">
                  <div className="tooltip-header">
                    {_t("expense_type_and_amount", "Expense Type & Amount")}
                  </div>
                  <ul className="tooltip-list">
                    {row.items?.map((item, index) => (
                      <li key={index} className="tooltip-item">
                        <span className=""> {_t(item.expense_type , item.expense_type)}</span>
                        <span className="tooltip-amount">${item.amount}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Tooltip>
            )}
          </Overlay>
        </span>
      ),
    },
    {
      label: _t("bill_date", "Bill Date"),
      accessor: "bill_date",
      cell: ({ row }) => <span>{convertToUserTimezone(row.bill_date)}</span>,
    },
    {
      label: _t("due_date", "Due Date"),
      accessor: "due_date",
      cell: ({ row }) => <span>{convertToUserTimezone(row.due_date)}</span>,
    },
    {
      label: _t("bill_amount", "Bill Amount"),
      accessor: "total_amount",
      cell: ({ row }) => <span>${formatNumber(row.total_amount || 0)}</span>,
    },
    {
      label: _t("status", "Status"),
      accessor: "status",
      cell: ({ row }) => {
        // Default to "Unpaid" if status is not provided
        const currentStatus = row.status || "Unpaid";
        const isLoading = loadingStatusId === row.id;

        return (
          <>
            {currentStatus === "paid" ? (
              <span className="cursor-pointer cmn_badge text-center px-4">
                {isLoading
                  ? `${_t("updating", "Updating")}...`
                  : currentStatus.toUpperCase()}
              </span>
            ) : (
              <Dropdown className="select_status">
                <Dropdown.Toggle
                  variant="unset"
                  size="sm"
                  className="d-flex gap-2 align-items-center justify-content-center"
                  disabled={isLoading}
                >
                  {isLoading
                    ? `${_t("updating", "Updating")}...`
                    : _t(currentStatus.toUpperCase() , currentStatus.toUpperCase()) }{" "}
                  {OrangeArrow}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() =>
                      confirmChangeStatusPrompt(row.id, row.bill_number, "Paid")
                    }
                    disabled={isLoading}
                  >
                    {_t("Paid", "Paid")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setShowStatusConfirmationModal(false);
                    }}
                    disabled={isLoading}
                  >
                    {_t("Unpaid", "Unpaid")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </>
        );
      },
    },
    {
      label: _t("is_registered", "Is Registered"),
      accessor: "is_registered",
      cell: ({ row }) => (
        <span>
          {row.is_registered == "1" ? _t("yes", "Yes") : _t("no", "No")}
        </span>
      ),
    },
    {
      label: _t("invoice", "Invoice"),
      accessor: "invoice_file_url",
      cell: ({ row }) => {
        if (row.invoice_file_url) {
          return (
            <div className="position-relative file_view">
              <a
                href={row.invoice_file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                {Invoice}
              </a>
              <button
                type="button"
                onClick={() => {
                  setDelInvoiceRecordId(row.id);
                  setShowInvoiceDelModal(true);
                }}
              >
                X
              </button>
            </div>
          );
        } else {
          if (isUploadingInvoice && selectedInvoiceRecordId == row.id) {
            return `${_t("uploading", "Uploading")}...`;
          }
          return (
            <div className="position-relative file_view cursor-pointer">
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange.bind(this, row.id)}
                ref={(el) => (fileInputRefs.current[row.id] = el)}
                className="mw-100"
              />
              <span>
                <UploadSimpleSvg />
              </span>
            </div>
          );
        }
      },
    },
    {
      label: _t("receipt", "Receipt"),
      accessor: "receipt_file_url",
      cell: ({ row }) => {
        if (row.receipt_file_url) {
          return (
            <div className="position-relative file_view">
              <a
                href={row.receipt_file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                {Invoice}
              </a>
              <button
                type="button"
                onClick={() => {
                  setDelReceiptRecordId(row.id);
                  setShowReceiptDelModal(true);
                }}
              >
                X
              </button>
            </div>
          );
        } else {
          if (isUploadingReceipt && selectedReceiptRecordId == row.id) {
            return `${_t("uploading", "Uploading")}...`;
          }
          return (
            <div className="position-relative file_view cursor-pointer">
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/jpg"
                onChange={handleReceiptFileChange.bind(this, row.id)}
                ref={(el) => (receiptFileInputRefs.current[row.id] = el)}
                className="mw-100"
              />
              <span>
                <UploadSimpleSvg />
              </span>
            </div>
          );
        }
      },
    },
    {
      label: _t("action", "Action"),
      accessor: "action",
      cell: ({ row }) => {
        const isLoading = loadingStatusId === row.id;
        const currentStatus = row.status || "unpaid";

        return (
          <div className="d-flex gap-3">
            {isLoading ? (
              <span className="text-muted">{_t("loading", "Loading")}</span>
            ) : (
              <>
                <span
                  className="cursor-pointer"
                  onClick={() => handleViewExpense(row.id)}
                  title={_t("view_details", "View Details")}
                >
                  {EyeView}
                </span>
                {currentStatus !== "paid" && (
                  <>
                    <span
                      className="cursor-pointer"
                      onClick={() => handleEditExpense(row.id)}
                      title={_t("edit", "Edit")}
                    >
                      {EditBlue}
                    </span>
                    <span
                      className="cursor-pointer"
                      onClick={() => handleDeleteExpense(row)}
                      title={_t("delete", "Delete")}
                    >
                      {DeleteLightRed}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  // Handle filter changes
  const handleFilterChange = (filters) => {
    setFilterParams((prev) => ({
      ...prev,
      ...filters,
    }));
  };

  const handleFileChange = async (id, event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        showToast(
          _t("only_pdf_allowed", "Only PDF files are allowed."),
          "error"
        );
        return;
      }
      try {
        setSelectedInvoiceRecordId(id);
        const formData = new FormData();
        formData.append("invoice", file);
        const result = await uploadExpenseInvoice({
          id,
          requestBody: formData,
        }).unwrap();
      } catch (error) {
      } finally {
        setSelectedInvoiceRecordId(null);
        const inputEl = fileInputRefs.current[id];
        if (inputEl) {
          inputEl.value = "";
        }
      }
    }
  };

  const handleReceiptFileChange = async (id, event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
      ];

      const MAX_SIZE_MB = 5;
      const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        showToast(
          _t(
            "only_pdf_or_image_allowed",
            "Only PDF or image files are allowed."
          ),
          "error"
        );
        return;
      }

      if (file.size > MAX_SIZE_BYTES) {
        showToast(
          _t(
            "file_too_large",
            `File must not be larger than ${MAX_SIZE_MB}MB.`
          ),
          "error"
        );
        return;
      }

      try {
        setSelectedReceiptRecordId(id);
        const formData = new FormData();
        formData.append("receipt", file);

        const result = await uploadExpenseReceipt({
          id,
          requestBody: formData,
        }).unwrap();
      } catch (error) {
        // Handle error (optional)
      } finally {
        setSelectedReceiptRecordId(null);
        const inputEl = receiptFileInputRefs.current[id];
        if (inputEl) {
          inputEl.value = "";
        }
      }
    }
  };

  const deleteInvoiceHandler = async () => {
    // Handle delete invoice logic here
    if (delInvoiceRecordId) {
      try {
        await removeExpenseInvoice(delInvoiceRecordId).unwrap();
      } catch (error) {
      } finally {
        setDelInvoiceRecordId(null);
        setShowInvoiceDelModal(false);
      }
    }
  };

  const deleteReceiptHandler = async () => {
    // Handle delete receipt logic here
    if (delReceiptRecordId) {
      try {
        await removeExpenseReceipt(delReceiptRecordId).unwrap();
      } catch (error) {
      } finally {
        setDelReceiptRecordId(null);
        setShowReceiptDelModal(false);
      }
    }
  };

  return (
    <>
      <CommonDeleteModal
        show={showConfirmationModal}
        handleClose={cancelDelete}
        title={_t("confirm_delete", "Confirm Delete")}
        message={`${_t(
          "confirm_delete_bill",
          "Are you sure you want to delete bill"
        )} #${expenseToDelete?.bill_number}?`}
        onConfirm={confirmDelete}
        isLoading={isRemoving}
      />

      <CommonDeleteModal
        show={showStatusConfirmationModal}
        handleClose={() => setShowStatusConfirmationModal(false)}
        title={_t("confirm_status_change", "Confirm Status Change")}
        message={`${_t(
          "confirm_mark_bill",
          "Are you sure you want to mark bill"
        )} #${pendingStatusChange.bill_number} as ${
          pendingStatusChange.newStatus
        }?`}
        onConfirm={async () => {
          if (pendingStatusChange.id && pendingStatusChange.newStatus) {
            await handleChangeStatus(
              pendingStatusChange.id,
              pendingStatusChange.newStatus
            );
          }
          setShowStatusConfirmationModal(false);
          setPendingStatusChange({
            id: null,
            bill_number: null,
            newStatus: null,
          });
        }}
        isLoading={isUpdating}
        buttonLabel={_t("confirm", "Confirm")}
      />

      <CommonDeleteModal
        show={showInvoiceDelModal}
        handleClose={() => {
          setShowInvoiceDelModal(false);
        }}
        title={_t("confirm_delete", "Confirm Delete")}
        message={`${_t(
          "are_you_sure_you_want_to_delete",
          "Are you sure you want to delete"
        )} ?`}
        onConfirm={deleteInvoiceHandler}
        isLoading={isRemovingInvoice}
      />

      <CommonDeleteModal
        show={showReceiptDelModal}
        handleClose={() => {
          setShowReceiptDelModal(false);
        }}
        title={_t("confirm_delete", "Confirm Delete")}
        message={`${_t(
          "are_you_sure_you_want_to_delete",
          "Are you sure you want to delete"
        )} ?`}
        onConfirm={deleteReceiptHandler}
        isLoading={isRemovingReceipt}
      />

      {/* Bill View Modal */}
      <BillViewModal
        show={showBillViewModal}
        handleClose={handleCloseBillViewModal}
        billData={billQueryData?.data ?? selectedBillData}
        setShow={setShowBillViewModal}
      />

      <Datatable
        title={_t("unpaid_bills", "Unpaid Bills")}
        columns={columns}
        tableData={expenseData?.data || { data: [] }}
        pagination
        isLoading={isLoading}
        showFilterButton={true}
        showCount={true}
        onPageChange={handlePageChange}
        showFilter={setFilterParams}
        filterModule="billPaidListing"
        buttons={[
          {
            label: _t("ad_expense", "Add Expense"),
            icon: PlusSvg,
            onClick: () => {
              navigate("/bill-creation");
            },
          },
        ]}
        isLoading={isLoading}
      />

      {/* Create container refs for each tooltip */}
      {expenseData?.data?.data?.map((row) => (
        <div
          key={`tooltip-container-${row.id}`}
          ref={tooltipContainerRefs[row.id]}
          style={{ position: "relative" }}
        />
      ))}
    </>
  );
};

export default BillListing;
